import React, { useEffect, useMemo } from 'react'
import { styles } from '../../../../../../styles/tailwind-config'
import { Button } from '../../../../../../components/ui/button'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../../../../../../context/AuthProvider'
import axios from '../../../../../../lib/axios'
import { Accordion, AccordionContent, AccordionItem } from '../../../../../../components/ui/accordion'
import { useBoundStore } from '../../../../../../zustand'
import { useInterventionInput } from '../../../../../../context/InterventionInputContext'
import { Loader2 } from 'lucide-react'
import { Trans, useLingui } from '@lingui/react/macro'

type InterventionHistoryResponse = {
  page: number
  totalrecords: number
  totalpages: number
  interventions: Intervention[]
}

const History = () => {
  const { auth } = useAuth()
  const { t } = useLingui()
  const [searchParams, setSearchParams] = useSearchParams()
  const technicians = useBoundStore((state) => state.technicians)
  const { intervention } = useInterventionInput()

  const pageFromUrl = Number(searchParams.get('page') ?? 1)
  const page = Number.isFinite(pageFromUrl) && pageFromUrl > 0 ? pageFromUrl : 1

  const dbid = auth.data?.databases?.[0]?.dbid
  const klantid = intervention?.m_iKLANTID

  const queryKey = useMemo(
    () => ['intervention-history', { dbid, klantid, page }],
    [dbid, klantid, page]
  )

  const { data, isLoading, isFetching, isError } = useQuery<
    InterventionHistoryResponse,
    Error,
    InterventionHistoryResponse
  >({
    queryKey,
    queryFn: async () => {
      const resp = await axios.get<InterventionHistoryResponse>(
        `intervention/history?dbid=${dbid}&klantid=${klantid}&page=${page}`
      )
      return resp.data
    },
    enabled: !!dbid && !!klantid,
    placeholderData: (prev) => prev,
    gcTime: 5 * 60 * 1000,
  })

  const interventions = data?.interventions ?? []
  const totalPages = data?.totalpages ?? 0
  const totalRecords = data?.totalrecords ?? 0

  useEffect(() => {
    if (!data) return
    if (totalPages > 0 && page > totalPages) {
      const next = new URLSearchParams(searchParams)
      next.set('page', String(totalPages))
      setSearchParams(next, { replace: true })
    }
  }, [data, page, totalPages, searchParams, setSearchParams])

  const canPrev = page > 1
  const canNext = totalPages > 0 && page < totalPages

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams)
    next.set(key, value)
    setSearchParams(next, { replace: true })
  }

  return (
    <div>
      <h3 className={`${styles.h3} mb-2`}><Trans>Vorige bezoeken</Trans></h3>

      <div className="mb-4 flex items-center justify-between text-sm text-neutral-600">
        <div className="flex items-center gap-2">
          {isFetching && <Loader2 className="w-4 h-4 animate-spin" />}
          {isLoading && !data && <Trans>Even geduld...</Trans>}
          {!isLoading && (
            <span>
              <Trans>
                {totalRecords} resultaten — pagina {Math.min(page, totalPages || 1)}/{totalPages || 1}
              </Trans>
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            disabled={!canPrev}
            onClick={() => setParam('page', String(page - 1))}
          >
            ‹ <Trans>Vorige</Trans>
          </Button>
          <Button
            variant="outline"
            disabled={!canNext}
            onClick={() => setParam('page', String(page + 1))}
          >
            <Trans>Volgende</Trans> ›
          </Button>
        </div>
      </div>

      {isError && (
        <p className="text-sm text-red-600"><Trans>Kon de historiek niet laden.</Trans></p>
      )}

      <Accordion type="multiple" defaultValue={['previous-visits']} className="flex flex-col gap-2">
        <AccordionItem value="previous-visits">
          <AccordionContent className="space-y-6">
            {interventions.length > 0 && (
              <>
                {interventions.map((history) => {
                  const technician = technicians?.find(
                    (t: any) => t?.m_iTECHNIEKERID?.toString() === history?.m_iTECHNIEKERID?.toString()
                  )

                  return (
                    <div key={`history-${history.m_iINTERVENTIESID}`}>
                      <div className="flex items-center justify-between py-2 border-b">
                        <p className="font-bold">{technician?.m_sNAAM}</p>
                        <p className="text-neutral-400">{history?.m_dDATUM || t`Geen datum`}</p>
                      </div>
                      <div className="py-2">
                        <p className="font-medium text-neutral-500"><Trans>Reden</Trans></p>
                        <p className="font-medium text-neutral-800">{history?.m_sOMSCHRIJVING || t`Geen reden`}</p>
                      </div>
                      <div className="py-2">
                        <p className="font-medium text-neutral-500"><Trans>Geboden oplossing</Trans></p>
                        {(history?.m_sOPLOSSING || history?.m_sOPLOSSINGEXTRA)
                          ? <p className="font-medium text-neutral-800">{history?.m_sOPLOSSING} {history?.m_sOPLOSSINGEXTRA}</p>
                          : <p className="font-medium text-neutral-800"><Trans>Geen oplossing</Trans></p>
                        }
                      </div>
                      <div className="py-2">
                        <p className="font-medium text-neutral-500"><Trans>Interne commentaar</Trans></p>
                        <p className="font-medium text-neutral-800">{history?.m_sINTCOMM || t`Geen interne commentaar`}</p>
                      </div>
                      <Link to={`/intervention/bezoekrapport?interventionid=${history?.m_iINTERVENTIESID}`}>
                        <Button variant="secondary" className="w-full"><Trans>Bezoekrapport bekijken</Trans></Button>
                      </Link>
                    </div>
                  )
                })}
              </>
            )}

            {!isLoading && interventions.length === 0 && (
              <p className="text-sm text-neutral-500"><Trans>Geen bezoeken gevonden.</Trans></p>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  )
}

export default History
